import { Dialog } from "primereact/dialog";
import { useTranslation } from "react-i18next";
import FormSection from "../../../components/form-section/FormSection.component";
import { Form } from "react-final-form";
import { useState, useEffect, useRef } from "react";
import UiButton from "../../../components/button/Button";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import UiInputText from "../../../components/input-text/InputText.component";
import UiSelect from "../../../components/select/Select.component";
import UiInputDate from "../../../components/input-date/InputDate.component";
import UiSelectButton from "../../../components/select-button/SelectButton.component";
import {
	eParticipantStatus,
	ParticipantProfile,
	UnenrollmentReason,
	UnreachableReason,
} from "../../../models/entities/participant.model";
import { ListOption } from "../../../models/misc.model";
import { Validations } from "../../../services/form/validations.service";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { Formatter } from "../../../services/formatter/formatter.service";
import { FormApi } from "final-form";

export interface ModalProfileEventsProps {
	isVisible: boolean;
	participantId: number;
	mode: "OPT_OUT" | "OPT_IN" | "ENROLLED" | "SNOOZE" | "UNSNOOZE" | "UNREACHABLE" | "REACHABLE";
	onComplete: (e: ParticipantProfile | null) => void;
	onClose: () => void;
}

/**
 * OPT_IN:
 * - Mark as contactable
 * - Create event: PARTICIPANT_OPT_IN
 * - Status changes to: ACTIVE / PROSPECTIVE
 *
 * OPT_OUT:
 * - Mark as do not contact
 * - Create event: PARTICIPANT_OPT_OUT
 * - Status changes to: DNC
 *
 * ENROLLED:
 * - Create event: PARTICIPANT_ENROLLED
 * - Status changes to: ACTIVE
 * - Sets enrollment date
 */

const ModalProfileEvents = (props: ModalProfileEventsProps) => {
	const { t } = useTranslation("common");

	const [message, setMessage] = useState(false);
	const [formData, setFormData] = useState<{
		unenrollmentReason: string | null;
		unenrollmentOtherReason: string | null;
		unenrollmentAdditionalComments: string | null;
		unreachableReason: string | null;
		unreachableOtherReason: string | null;
		unreachableAdditionalComments: string | null;
		nextReachoutDate: Date | null;
		optionValue: number;
	}>({
		unenrollmentReason: null,
		unenrollmentOtherReason: null,
		unenrollmentAdditionalComments: null,
		unreachableReason: null,
		unreachableOtherReason: null,
		unreachableAdditionalComments: null,
		nextReachoutDate: null,
		optionValue: 0,
	});
	const formApiRef = useRef<FormApi<any, Partial<any>> | null>(null);

	function parseDateIgnoringTimezone(isoString: string) {
		const [year, month, day] = isoString.split("T")[0].split("-").map(Number);
		return new Date(year, month - 1, day);
	}
	// Fetch participant data to get current nextReachoutDate when snooze modal opens
	useEffect(() => {
		if (props.mode === "SNOOZE" && props.isVisible) {
			EndpointsService.dataRetriever
				.getParticipantById({ participantId: props.participantId })
				.then((response) => {
					const currentDate = response.nextReachoutDate;
					setFormData((prev) => ({
						...prev,
						nextReachoutDate: currentDate
							? parseDateIgnoringTimezone(currentDate)
							: null,
					}));
				})
				.catch(() => {
					// Do nothing on error
				});
		}
	}, [props.mode, props.isVisible, props.participantId]);

	// Sync formData.nextReachoutDate with form field when it changes
	useEffect(() => {
		if (formApiRef.current && formData.nextReachoutDate !== undefined) {
			const currentFieldValue = formApiRef.current.getFieldState("nextReachoutDate")?.value;
			if (
				currentFieldValue === undefined ||
				currentFieldValue === null ||
				formData.nextReachoutDate === null ||
				(currentFieldValue instanceof Date &&
					formData.nextReachoutDate instanceof Date &&
					currentFieldValue.getTime() !== formData.nextReachoutDate.getTime())
			) {
				formApiRef.current.change("nextReachoutDate", formData.nextReachoutDate);
			}
		}
	}, [formData.nextReachoutDate]);

	const handleSubmit = async () => {
		setMessage(false);
		if (props.mode === "OPT_OUT") {
			await onOptOut();
		} else if (props.mode === "OPT_IN") {
			await onOptIn();
		} else if (props.mode === "ENROLLED") {
			await onEnrolled();
		} else if (props.mode === "SNOOZE") {
			await onSnooze();
		} else if (props.mode === "UNSNOOZE") {
			await onUnsnooze();
		} else if (props.mode === "UNREACHABLE") {
			await onUnreachable();
		} else if (props.mode === "REACHABLE") {
			await onReachable();
		}
	};

	const onOptOut = async () => {
		// This endpoint internally sends the PARTICIPANT_OPT_OUT event
		await EndpointsService.dataRetriever
			.updateProfileKannactInfo({
				body: {
					participantId: props.participantId,
					status: eParticipantStatus.DNC,
					unenrollmentReason: formData.unenrollmentReason,
					unenrollmentOtherReason: formData.unenrollmentOtherReason,
					unenrollmentAdditionalComments: formData.unenrollmentAdditionalComments,
				},
			})
			.then((e) => props.onComplete(e))
			.catch(() => setMessage(true));
	};

	const onOptIn = async () => {
		await EndpointsService.dataRetriever
			.participantOptIn({
				config: {
					params: { id: props.participantId },
				},
			})
			.then(() => props.onComplete(null))
			.catch(() => setMessage(true));
	};

	const onEnrolled = async () => {
		// This endpoint internally sends the PARTICIPANT_OPT_OUT event
		await EndpointsService.dataRetriever
			.updateProfileKannactInfo({
				body: {
					participantId: props.participantId,
					status: eParticipantStatus.ACTIVE,
				},
			})
			.then((e) => props.onComplete(e))
			.catch(() => setMessage(true));
	};

	const dateOptions = [
		{ id: 1, label: "UI_COMPONENTS.CALENDAR.ONE_DAY" },
		{ id: 3, label: "UI_COMPONENTS.CALENDAR.THREE_DAYS" },
		{ id: 5, label: "UI_COMPONENTS.CALENDAR.FIVE_DAYS" },
		{ id: 7, label: "UI_COMPONENTS.CALENDAR.ONE_WEEK" },
		{ id: 14, label: "UI_COMPONENTS.CALENDAR.TWO_WEEKS" },
		{ id: 30, label: "UI_COMPONENTS.CALENDAR.ONE_MONTH" },
		{ id: 0, label: "UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.CUSTOM" },
	];

	const calculateDateFromOption = (e: any) => {
		let nextDate = new Date();
		const dayToMilliseconds = 24 * 60 * 60 * 1000;
		if (e.value !== 0) {
			nextDate = new Date(nextDate.getTime() + e.value * dayToMilliseconds);
			// Adjust to next working day if weekend
			nextDate = Formatter.getNextWorkingDate(nextDate)!;
		}

		setFormData({
			...formData,
			nextReachoutDate: nextDate,
			optionValue: e.value,
		});
	};

	const onSnooze = async () => {
		// Update status first
		await EndpointsService.dataRetriever
			.updateProfileKannactInfo({
				body: {
					participantId: props.participantId,
					status: eParticipantStatus.SNOOZED,
				},
			})
			.then(async (e) => {
				// Then update next reachout date if provided (using updateProfileGeneralInfo as shown in ParticipantProfile)
				await EndpointsService.dataRetriever
					.updateProfileGeneralInfo({
						body: {
							participantId: props.participantId,
							nextReachoutDate: formData.nextReachoutDate
								? new Date(
										Formatter.dateToISO(formData.nextReachoutDate!).split(
											"T"
										)[0]
									)
								: null,
						} as any,
					})
					.catch(() => {
						// If next reachout date update fails, still complete with status update
					});
				props.onComplete(e);
			})
			.catch(() => setMessage(true));
	};

	const onUnsnooze = async () => {
		// Use opt-in method to restore participant to contactable status
		await EndpointsService.dataRetriever
			.participantOptIn({
				config: {
					params: { id: props.participantId },
				},
			})
			.then(() => props.onComplete(null))
			.catch(() => setMessage(true));
	};

	const onUnreachable = async () => {
		await EndpointsService.dataRetriever
			.updateProfileKannactInfo({
				body: {
					participantId: props.participantId,
					status: eParticipantStatus.UNREACHABLE,
					unreachableReason: formData.unreachableReason,
					unreachableOtherReason: formData.unreachableOtherReason,
					unreachableAdditionalComments: formData.unreachableAdditionalComments,
				},
			})
			.then((e) => props.onComplete(e))
			.catch(() => setMessage(true));
	};

	const onReachable = async () => {
		// Use opt-in method to restore participant to contactable status
		await EndpointsService.dataRetriever
			.participantOptIn({
				config: {
					params: { id: props.participantId },
				},
			})
			.then(() => props.onComplete(null))
			.catch(() => setMessage(true));
	};

	return (
		<Dialog
			header={t(
				props.mode === "OPT_OUT"
					? "PARTICIPANT.DETAILS.MODALS.TITLE_OPT_OUT"
					: props.mode === "OPT_IN"
						? "PARTICIPANT.DETAILS.MODALS.TITLE_OPT_IN"
						: props.mode === "ENROLLED"
							? "PARTICIPANT.DETAILS.MODALS.TITLE_ENROLLED"
							: props.mode === "SNOOZE"
								? "PARTICIPANT.DETAILS.MODALS.TITLE_SNOOZE"
								: props.mode === "UNSNOOZE"
									? "PARTICIPANT.DETAILS.MODALS.TITLE_UNSNOOZE"
									: props.mode === "UNREACHABLE"
										? "PARTICIPANT.DETAILS.MODALS.TITLE_UNREACHABLE"
										: "PARTICIPANT.DETAILS.MODALS.TITLE_REACHABLE"
			)}
			visible={props.isVisible}
			breakpoints={{ "960px": "75vw", "640px": "100vw" }}
			style={{ width: "70vw" }}
			modal={true}
			focusOnShow={false}
			onHide={props.onClose}
		>
			<Form
				onSubmit={handleSubmit}
				initialValues={formData}
				render={({ handleSubmit, form, submitting }) => {
					formApiRef.current = form;
					return (
						<form onSubmit={handleSubmit}>
							{/* OPT_IN */}
							{props.mode === "OPT_IN" && (
								<p>{t("PARTICIPANT.DETAILS.MODALS.MSG_OPT_IN")}</p>
							)}

							{/* ENROLLED */}
							{props.mode === "ENROLLED" && (
								<p>{t("PARTICIPANT.DETAILS.MODALS.MSG_ENROLLED")}</p>
							)}

							{/* SNOOZE */}
							{props.mode === "SNOOZE" && (
								<>
									<p className="mb-4">
										{t("PARTICIPANT.DETAILS.MODALS.MSG_SNOOZE")}
									</p>
									<FormSection className="row">
										{/* Date Picker and Options Container */}
										<div className="col-12 col-sm-12 col-md-12 col-lg-8">
											<div className="participant-modal-reminders__date-input-container">
												{/* Date Picker - Left Section */}
												<div>
													<UiInputDate
														id="nextReachoutDate"
														name="nextReachoutDate"
														label="UI_COMPONENTS.MODAL_NEXT_REACHOUT_DATE.FORM_FIELDS.dateOn"
														onChange={(e) => {
															const selectedDate = e.target
																.value as Date;
															// Don't adjust weekend dates when manually selected
															setFormData({
																...formData,
																nextReachoutDate: selectedDate,
																optionValue: 0,
															});
														}}
														minDate={new Date()}
														validations={[]}
														disabled={submitting}
														inline={true}
													/>
													<p
														className="mt-2"
														style={{ fontSize: "14px" }}
													>
														{t(
															"UI_COMPONENTS.MODAL_NEXT_REACHOUT_DATE.FORM_FIELDS.DATE_WILL_BE_SET_TO"
														)}{" "}
														{formData.nextReachoutDate ? (
															<span
																style={{
																	fontWeight: "bold",
																	color: "#000",
																}}
															>
																{Formatter.date(
																	formData.nextReachoutDate.toISOString()
																)}
															</span>
														) : (
															<span style={{ color: "#000" }}>-</span>
														)}
													</p>
												</div>

												{/* Quick Options - Right Section */}
												<div
													className="col-12 col-sm-12 col-md-12 col-lg-6"
													style={{ minWidth: "250px", flex: "0 0 auto" }}
												>
													<p className="secondary-title">
														{t(
															"UI_COMPONENTS.MODAL_NEXT_REACHOUT_DATE.FORM_FIELDS.OPTIONS"
														)}
													</p>
													<div>
														<UiSelectButton
															id="optionValue"
															className="vertical"
															name="optionValue"
															onChange={calculateDateFromOption}
															options={dateOptions}
														/>
													</div>
												</div>
											</div>
										</div>
									</FormSection>
								</>
							)}

							{/* UNSNOOZE */}
							{props.mode === "UNSNOOZE" && (
								<p>{t("PARTICIPANT.DETAILS.MODALS.MSG_UNSNOOZE")}</p>
							)}

							{/* UNREACHABLE */}
							{props.mode === "UNREACHABLE" && (
								<>
									<div className="mb-4">
										{t("PARTICIPANT.DETAILS.MODALS.MSG_UNREACHABLE")}
									</div>

									<FormSection className="row">
										<div className="col-12 col-md-8 col-lg-8 col-xl-6 col-xxl-6">
											<UiSelect
												id="unreachableReason"
												label="PARTICIPANT.DETAILS.MODALS.PLACEHOLDER_UNREACHABLE"
												name="unreachableReason"
												onChange={(e) =>
													setFormData({
														...formData,
														unreachableReason: e.target.value,
													})
												}
												options={Object.keys(UnreachableReason).map(
													(x) =>
														new ListOption({
															id: x,
															label: `ENUMS.UNREACHABLE_REASON.${x}`,
														})
												)}
												disabled={submitting}
												validations={[Validations.required]}
											/>
										</div>

										{formData.unreachableReason === "OTHER" && (
											<div className="col-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">
												<UiInputText
													id="unreachableOtherReason"
													label="PARTICIPANT.DETAILS.KANNACT_INFO.FORM_FIELDS.unreachableOtherReason"
													name="unreachableOtherReason"
													onChange={(e) =>
														setFormData({
															...formData,
															unreachableOtherReason: e.target.value,
														})
													}
													validations={
														formData.unreachableReason ===
														UnreachableReason.OTHER
															? [Validations.required]
															: []
													}
													disabled={submitting}
												/>
											</div>
										)}
										<div className="col-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">
											<UiInputText
												id="unreachableAdditionalComments"
												label="PARTICIPANT.DETAILS.KANNACT_INFO.FORM_FIELDS.unreachableAdditionalComments"
												name="unreachableAdditionalComments"
												removeBottomSpacer={true}
												onChange={(e) =>
													setFormData({
														...formData,
														unreachableAdditionalComments:
															e.target.value,
													})
												}
												validations={[]}
												disabled={submitting}
											/>
										</div>
									</FormSection>
								</>
							)}

							{/* REACHABLE */}
							{props.mode === "REACHABLE" && (
								<p>{t("PARTICIPANT.DETAILS.MODALS.MSG_REACHABLE")}</p>
							)}

							{/* OPT_OUT */}
							{props.mode === "OPT_OUT" && (
								<>
									{/* Message */}
									<div className="mb-4">
										{t("PARTICIPANT.DETAILS.MODALS.MSG_OPT_OUT")}
									</div>

									<FormSection className="row">
										<div className="col-12 col-md-8 col-lg-8 col-xl-6 col-xxl-6">
											<UiSelect
												id="unenrollmentReason"
												label="PARTICIPANT.DETAILS.MODALS.PLACEHOLDER_OPT_OUT"
												name="unenrollmentReason"
												onChange={(e) =>
													setFormData({
														...formData,
														unenrollmentReason: e.target.value,
													})
												}
												options={Object.keys(UnenrollmentReason).map(
													(x) =>
														new ListOption({
															id: x,
															label: `ENUMS.UNENROLLMENT_REASON.${x}`,
														})
												)}
												disabled={submitting}
												validations={[Validations.required]}
											/>
										</div>

										{formData.unenrollmentReason === "OTHER" && (
											<div className="col-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">
												<UiInputText
													id="unenrollmentOtherReason"
													label="PARTICIPANT.DETAILS.KANNACT_INFO.FORM_FIELDS.unenrollmentOtherReason"
													name="unenrollmentOtherReason"
													onChange={(e) =>
														setFormData({
															...formData,
															unenrollmentOtherReason: e.target.value,
														})
													}
													validations={
														formData.unenrollmentReason ===
														UnenrollmentReason.OTHER
															? [Validations.required]
															: []
													}
													disabled={submitting}
												/>
											</div>
										)}
										<div className="col-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">
											<UiInputText
												id="unenrollmentAdditionalComments"
												label="PARTICIPANT.DETAILS.KANNACT_INFO.FORM_FIELDS.unenrollmentAdditionalComments"
												name="unenrollmentAdditionalComments"
												removeBottomSpacer={true}
												onChange={(e) =>
													setFormData({
														...formData,
														unenrollmentAdditionalComments:
															e.target.value,
													})
												}
												validations={[]}
												disabled={submitting}
											/>
										</div>
									</FormSection>
								</>
							)}

							{message && (
								<div className="form-message">
									<UiFieldMessage
										severity={FieldMessageSeverity.DANGER}
										label="PARTICIPANT.DETAILS.SUBMIT_ERROR"
									/>
								</div>
							)}

							<div className="action-buttons">
								<UiButton
									label="UI_COMPONENTS.BUTTONS.NEVERMIND"
									type="button"
									className="p-button-outlined p-button-rounded"
									onClick={props.onClose}
									disabled={submitting}
								/>
								<UiButton
									label={
										submitting
											? "UI_COMPONENTS.BUTTONS.SAVING"
											: "UI_COMPONENTS.BUTTONS.CONFIRM"
									}
									className="p-button-rounded"
									type="submit"
									loading={submitting}
									disabled={submitting}
									onClick={handleSubmit}
								/>
							</div>
						</form>
					);
				}}
			/>
		</Dialog>
	);
};

export default ModalProfileEvents;
